import { useCallback, useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function BroadcastGate() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/broadcasts/pending");
      setPending(data.items || []);
    } catch {
      /* never block the app on a fetch error */
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [user, load]);

  if (!user || pending.length === 0) return null;

  const ack = async (id) => {
    setBusy(id);
    try {
      await api.post(`/broadcasts/${id}/acknowledge`);
      setPending((p) => p.filter((b) => b.id !== id));
      toast.success("Message acknowledged");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="broadcast-gate" className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/85 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100">
            <Megaphone className="h-5 w-5 text-red-700" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900" data-testid="broadcast-gate-title">Urgent message from the office</h1>
            <p className="text-xs text-slate-500">
              You must read and acknowledge {pending.length > 1 ? "each message" : "this message"} before continuing.
            </p>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          {pending.map((b) => (
            <div key={b.id} className="rounded-lg border-2 border-red-200 bg-red-50/50 p-4" data-testid={`broadcast-item-${b.id}`}>
              <p className="font-semibold text-slate-900 text-sm">{b.title}</p>
              <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{b.body}</p>
              <p className="mt-2 text-xs text-slate-500">
                From {b.created_by_name} · {new Date(b.created_at).toLocaleString("en-GB")}
              </p>
              <button
                type="button"
                data-testid={`broadcast-ack-${b.id}`}
                onClick={() => ack(b.id)}
                disabled={busy === b.id}
                className="mt-3 min-h-[44px] w-full rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-700 disabled:opacity-60"
              >
                {busy === b.id ? "Recording…" : "I have read and acknowledge this message"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
